import { MutableRefObject, useEffect, useRef } from 'react';
import axios, { HttpStatusCode } from 'axios';
import type { AxiosInstance } from 'axios';

import { useKeycloak } from '@react-keycloak/web';
import { selectEnvironment } from '../state/environmentSlice';
import { useAppSelector } from './useAppSelector';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../routes/AppRouter';

export const useAxios = (kcToken: string): MutableRefObject<AxiosInstance | undefined> => {
  const navigate = useNavigate();
  const axiosInstance: MutableRefObject<AxiosInstance | undefined> = useRef<AxiosInstance>();
  const baseURL: string | undefined = useAppSelector(selectEnvironment)?.env?.backendUrl;
  const { initialized } = useKeycloak();

  useEffect(() => {
    axiosInstance.current = axios.create({
      baseURL,
      headers: {
        Authorization: initialized ? `Bearer ${ kcToken }` : undefined,
        Accept: 'application/json'
      },
    });

    axiosInstance.current.interceptors.response.use(
      response => response,
      error => {
        if (!error.response) {
          console.error(error.message, error);
          navigate(APP_ROUTES.error);
        }
        if (error.response) {
          switch (error.response.status) {
            case HttpStatusCode.BadRequest:
              return {
                data: null,
                hasError: true,
                error: [error.response.data],
              };
            case HttpStatusCode.Unauthorized:
              // Authenticate again
              console.error(error.message, error);
              navigate(APP_ROUTES.home);
              break;
            case HttpStatusCode.InternalServerError:
              // Navigate to service error page
              console.error(error.message, error);
              navigate(APP_ROUTES.error);
              break;
          }
        }
        return error;
      }
    );

    return () => {
      axiosInstance.current = undefined;
    };
  }, [baseURL, initialized, kcToken]);

  return axiosInstance;
};